import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_HREF } from '@nrx/booking-ui';
import { Alert, Box, Button, Flex, Heading, Link, Spinner, Text } from '@nrx/ui';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { type FormEvent, type JSX, useEffect, useRef, useState } from 'react';

import { getApiBaseUrl } from '../../lib/api-base.js';
import { authFetch } from '../../lib/auth-fetch.js';
import { reportConsultError } from '../../lib/report-consult-error.js';
import { getStripe } from '../../lib/stripe.js';

const GENERIC_ERROR = 'Something went wrong. Please try again.';
const CONFIG_ERROR = 'Card capture is currently unavailable.';
/**
 * Shown when this component re-runs its setup after `onAuthorized` has already fired once for
 * this mount (see `authorizedOnceRef` in {@link AdminFeeCardStep}) — the server still reports the
 * gate as required, most likely FHIR search lag on the Observation this same flow just wrote, or a
 * write that raced. Minting a second SetupIntent here would hand the member who was just told
 * "Your card has been saved" a fresh, empty card form with no explanation.
 */
const STILL_GATED_ERROR =
  "We saved your card, but haven't been able to confirm it yet. Please contact us and we'll get you scheduled.";

type IntentState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; retryable: boolean }
  | { kind: 'ready'; clientSecret: string; setupIntentId: string };

/** Reads a string field off an unvalidated response body, `undefined` when it is not one. */
function readStringField(body: object, key: string): string | undefined {
  if (!(key in body)) return undefined;
  const value = (body as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
}

/** Reads the JSON body of a setup-intent response, throwing on anything unusable. */
async function parseSetupIntentResponse(
  res: Response,
): Promise<{ clientSecret: string; setupIntentId: string }> {
  if (!res.ok) {
    throw new Error(`setup-intent request failed (${res.status})`);
  }
  // `res.json()` throws on a non-JSON body — this app's own 403/404 → `/index.html` CloudFront
  // rewrite turns a misrouted request into an HTTP 200 carrying the SPA shell's HTML, which
  // `res.ok` alone would treat as success (see `api-base.ts`'s docblock for the same hazard).
  const body: unknown = await res.json();
  const clientSecret =
    typeof body === 'object' && body !== null ? readStringField(body, 'clientSecret') : undefined;
  const setupIntentId =
    typeof body === 'object' && body !== null ? readStringField(body, 'setupIntentId') : undefined;
  if (clientSecret === undefined || setupIntentId === undefined) {
    throw new Error('setup-intent response was missing clientSecret or setupIntentId');
  }
  return { clientSecret, setupIntentId };
}

/**
 * Reads the JSON body of a confirm response, throwing on anything unusable — same hazard and
 * same fix as {@link parseSetupIntentResponse}. Before this existed, only `res.ok` was checked:
 * an HTML 200 from the CloudFront rewrite above would have been accepted as a successful
 * confirmation, calling `onAuthorized()` on a member who was never actually un-gated and never
 * reporting the highest-value capture in this file (the confirmSetup-succeeded-but-confirm-failed
 * divergence) — silence exactly where the constraint requires signal.
 */
async function parseConfirmResponse(res: Response): Promise<{ recorded: true }> {
  if (!res.ok) {
    throw new Error(`confirm request failed (${res.status})`);
  }
  const body: unknown = await res.json();
  const recorded =
    typeof body === 'object' && body !== null && 'recorded' in body
      ? (body as Record<string, unknown>)['recorded']
      : undefined;
  if (recorded !== true) {
    throw new Error('confirm response did not report recorded: true');
  }
  return { recorded: true };
}

/**
 * Detects a return from Stripe's full-page 3-D Secure redirect: `confirmSetup`'s `return_url`
 * comes back here with `setup_intent` (+ `setup_intent_client_secret`, `redirect_status`) appended
 * to the query string by Stripe itself. `null` when this mount is not such a return.
 *
 * @remarks Unlike `apps/ffs-signup`'s `payment-resume.ts`, no sessionStorage layer is needed here:
 * the magic link this app is reached by is re-exchangeable (no `consumedAt`/single-use check — see
 * `reExchange`'s docblock), so a full page reload can re-derive everything except the one thing
 * Stripe itself hands back in the URL — the SetupIntent id.
 */
function readReturningSetupIntentId(search: string = window.location.search): string | null {
  try {
    const value = new URLSearchParams(search).get('setup_intent');
    return value !== null && value.trim() !== '' ? value : null;
  } catch {
    return null;
  }
}

/** Structural shape of a Stripe.js error — deliberately narrow, see {@link toReportableStripeError}. */
interface StripeErrorLike {
  readonly type?: unknown;
  readonly code?: unknown;
  readonly decline_code?: unknown;
}

/**
 * Builds a reportable `Error` from a Stripe.js error object, copying ONLY `type`, `code` and
 * `decline_code` — an ALLOWLIST, never a denylist.
 *
 * @remarks This is load-bearing, not a style choice. A real `StripeError`'s
 * `payment_method.billing_details` carries the member's name, email and phone — PHI this app must
 * never send to Sentry (W6). `report-consult-error.ts`'s `toError` happens to wrap any non-`Error`
 * value in a generic message today, which is what has kept this PHI-safe so far, but that is an
 * accident of the CURRENT implementation, not a contract either function documents or enforces.
 * Passing the raw Stripe error through — via a spread, a type widening, or a "helpful" refactor
 * that forwards the object — would open a PHI egress the moment `toError`'s behaviour ever
 * changes. Extend this allowlist only after checking what new fields the Stripe SDK version in use
 * actually carries.
 */
function toReportableStripeError(err: unknown): Error {
  const e = typeof err === 'object' && err !== null ? (err as StripeErrorLike) : {};
  const parts: string[] = [];
  if (typeof e.type === 'string') parts.push(`type=${e.type}`);
  if (typeof e.code === 'string') parts.push(`code=${e.code}`);
  if (typeof e.decline_code === 'string') parts.push(`decline_code=${e.decline_code}`);
  const wrapped = new Error(
    parts.length > 0 ? `Stripe error (${parts.join(', ')})` : 'Stripe error (no diagnostic fields)',
  );
  wrapped.name = 'StripeReportableError';
  return wrapped;
}

/**
 * Shared failure surface: a message plus the support phone, so a member who cannot complete the
 * card step always has an escape hatch (W4) rather than a dead end.
 */
function AdminFeeCardError({ message }: { message: string }): JSX.Element {
  return (
    <Alert tone="danger" className="consult-tab-alert">
      <Text as="p">{message}</Text>
      <Text as="p">
        Call us at{' '}
        <Link href={SUPPORT_PHONE_HREF} underline>
          <Text weight="medium">{SUPPORT_PHONE_DISPLAY}</Text>
        </Link>{' '}
        and we&apos;ll help you get booked.
      </Text>
    </Alert>
  );
}

/**
 * Card-on-file step for FFS members with a missed or late-cancelled consult: mints a Stripe
 * SetupIntent, mounts the Payment Element to collect the card, and confirms it server-side.
 *
 * @remarks Replaces the booking frames entirely while `adminFeeCardRequired` is true — see
 * `OneToOneTab`. **The Observation, not the browser, un-gates the member**: `onAuthorized` fires
 * only after the confirm POST resolves, never off `confirmSetup`'s client-side result alone, so a
 * card that Stripe accepted but this app failed to record does not silently lift the gate.
 *
 * This is the NourishedRx Stripe account, never Inbox Health's. The copy below must keep the
 * charge scoped to a missed or late-cancelled visit and must never imply the card pays for care —
 * that separation is the entire reason this feature does not reuse the Inbox Health card marker.
 */
export function AdminFeeCardStep({
  jwt,
  onAuthorized,
}: {
  jwt: string;
  onAuthorized: () => void;
}): JSX.Element {
  const [state, setState] = useState<IntentState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  // Held in state because `getStripe()` is what triggers the load, and this component is the only
  // thing in the SPA that needs Stripe.js — see `stripe.ts` for why importing it is deferred.
  const [stripe, setStripe] = useState<Promise<Stripe | null> | null>(null);
  // Set once `onAuthorized` has fired for THIS member (see the wrapped callback below). A
  // re-exchange after authorization can still come back gated — FHIR search lag on the
  // Observation this flow just wrote, or a write that raced — which hands this component a new
  // `jwt` and re-runs this effect. Without this flag that re-run would silently mint a SECOND
  // SetupIntent and show an empty card form to a member who was just told their card was saved.
  const authorizedOnceRef = useRef(false);

  useEffect(() => {
    if (authorizedOnceRef.current) {
      setState({ kind: 'error', message: STILL_GATED_ERROR, retryable: false });
      return;
    }

    // A return from Stripe's full-page 3-D Secure redirect: confirm the intent Stripe already
    // handed back in the URL, rather than minting a fresh one and showing an empty form to a
    // member who already entered their card once.
    const resumeSetupIntentId = readReturningSetupIntentId();
    if (resumeSetupIntentId !== null) {
      // Strip Stripe's query params immediately, so a reload of this URL doesn't re-confirm.
      window.history.replaceState({}, document.title, window.location.pathname);
      let resumeCancelled = false;
      setState({ kind: 'loading' });
      authFetch(jwt, `${getApiBaseUrl()}/payment/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setupIntentId: resumeSetupIntentId }),
      })
        .then(parseConfirmResponse)
        .then(() => {
          if (resumeCancelled) return;
          authorizedOnceRef.current = true;
          onAuthorized();
        })
        .catch((err: unknown) => {
          if (resumeCancelled) return;
          reportConsultError('admin-fee-confirm-resume', err);
          setState({ kind: 'error', message: GENERIC_ERROR, retryable: true });
        });
      return () => {
        resumeCancelled = true;
      };
    }

    // This call is what loads Stripe.js; nothing has fetched js.stripe.com before now. A `null`
    // means no key configured — skip the round-trip, there's nothing useful to do with an intent
    // we can't mount Elements for.
    const stripePromise = getStripe();
    if (stripePromise === null) {
      setState({ kind: 'error', message: CONFIG_ERROR, retryable: false });
      return;
    }
    setStripe(stripePromise);
    let cancelled = false;
    // Set the instant `stripePromise` rejects, so the fetch's OWN success handler below can bail
    // out rather than overwriting an already-known failure back to `'ready'`. Without this flag,
    // whichever settles LAST wins regardless of which is actually true: `stripePromise.catch`
    // below is a single microtask hop, while the fetch chain runs through `parseSetupIntentResponse`
    // (itself `async`, another hop via `res.json()`) — so an ALREADY-rejected `stripePromise`
    // resolves its `.catch` before an ALREADY-resolved fetch resolves its `.then`, and a naive
    // "last write wins" would silently restore the broken `'ready'` state.
    let stripeFailed = false;
    setState({ kind: 'loading' });

    // When Stripe.js fails to load (an ad blocker or corporate proxy blocking js.stripe.com),
    // `useStripe()`/`useElements()` inside `<Elements>` never resolve, so `AdminFeeCardForm`'s
    // submit button would stay disabled forever under an empty box — no error, no support number,
    // no way out. Forces the error state regardless of the setup-intent fetch's own outcome, and
    // does NOT report: `getStripe` already did, at scope `stripe-load`. Retryable because
    // `getStripe` clears its memo on failure, so the click re-attempts the load.
    stripePromise.catch(() => {
      stripeFailed = true;
      if (cancelled) return;
      setState({ kind: 'error', message: GENERIC_ERROR, retryable: true });
    });

    authFetch(jwt, `${getApiBaseUrl()}/payment/setup-intent`, { method: 'POST' })
      .then(parseSetupIntentResponse)
      .then(({ clientSecret, setupIntentId }) => {
        if (cancelled || stripeFailed) return;
        setState({ kind: 'ready', clientSecret, setupIntentId });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        reportConsultError('admin-fee-setup-intent', err);
        setState({ kind: 'error', message: GENERIC_ERROR, retryable: true });
      });
    return () => {
      cancelled = true;
    };
  }, [jwt, attempt]);

  return (
    <Box>
      <Heading as="h3" size="6" className="consult-section-title">
        <span className="consult-accent">Card on file for late cancellation fees</span>
      </Heading>
      <Box mb="5">
        <Text as="p">
          We noticed you missed or late-cancelled a visit with a NourishedRx RD. It happens! Before
          we schedule a next visit, we ask for a card to keep on file. This card will not be charged
          now and will only be charged if you miss or late cancel another appointment.
        </Text>
      </Box>

      {state.kind === 'loading' && (
        <Flex justify="center" py="8">
          <Spinner message="Preparing card entry…" />
        </Flex>
      )}

      {state.kind === 'error' && (
        <>
          <AdminFeeCardError message={state.message} />
          {state.retryable ? (
            <Box mt="4">
              <Button tone="secondary" onClick={() => setAttempt((n) => n + 1)}>
                Retry
              </Button>
            </Box>
          ) : null}
        </>
      )}

      {state.kind === 'ready' && (
        <Elements
          key={state.clientSecret}
          stripe={stripe}
          options={{ clientSecret: state.clientSecret }}
        >
          <AdminFeeCardForm
            jwt={jwt}
            setupIntentId={state.setupIntentId}
            onAuthorized={() => {
              // Marked BEFORE calling the parent, so a re-exchange that lands this component back
              // here (still gated, same mount) hits the `authorizedOnceRef.current` branch above
              // instead of minting a second SetupIntent — see that branch's comment.
              authorizedOnceRef.current = true;
              onAuthorized();
            }}
            onElementLoadError={(err) => {
              reportConsultError('admin-fee-element-load', toReportableStripeError(err));
              setState({ kind: 'error', message: GENERIC_ERROR, retryable: true });
            }}
          />
        </Elements>
      )}
    </Box>
  );
}

function AdminFeeCardForm({
  jwt,
  setupIntentId,
  onAuthorized,
  onElementLoadError,
}: {
  jwt: string;
  setupIntentId: string;
  onAuthorized: () => void;
  onElementLoadError: (err: unknown) => void;
}): JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authorized, setAuthorized] = useState(false);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setError(null);

    const submitResult = await elements.submit();
    if (submitResult.error) {
      setError(submitResult.error.message ?? GENERIC_ERROR);
      setSubmitting(false);
      return;
    }

    // `return_url` is required for a card that needs FULL-PAGE 3-D Secure (`redirect: 'if_required'`
    // only skips the redirect when Stripe can avoid one). Stripe appends `setup_intent` (+
    // `setup_intent_client_secret`, `redirect_status`) to this URL on return, which
    // `AdminFeeCardStep`'s effect reads via `readReturningSetupIntentId` to resume the confirm —
    // see that function's docblock for why no sessionStorage layer is needed here.
    const { error: confirmError } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (confirmError) {
      const isMemberFacing =
        confirmError.type === 'card_error' || confirmError.type === 'validation_error';
      // A declined card is a business outcome, not a defect. Capturing declines would make them
      // the dominant event volume here and bury real failures.
      if (!isMemberFacing) {
        reportConsultError('admin-fee-stripe-confirm', toReportableStripeError(confirmError));
      }
      setError(isMemberFacing ? (confirmError.message ?? GENERIC_ERROR) : GENERIC_ERROR);
      setSubmitting(false);
      return;
    }

    // The Observation, not the browser, un-gates the member: `onAuthorized` fires only once this
    // resolves, never off `confirmSetup`'s client-side result alone.
    try {
      const res = await authFetch(jwt, `${getApiBaseUrl()}/payment/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setupIntentId }),
      });
      await parseConfirmResponse(res);
    } catch (err: unknown) {
      // Highest-value capture in this file: confirmSetup already SUCCEEDED, so the card IS
      // stored at Stripe and only our record of it failed. A silent data inconsistency with no
      // signal in any other system.
      reportConsultError('admin-fee-confirm', err);
      setError(GENERIC_ERROR);
      setSubmitting(false);
      return;
    }

    setAuthorized(true);
    onAuthorized();
  }

  if (authorized) {
    return (
      <Alert tone="success" className="consult-tab-alert">
        <Text as="p" weight="medium">
          Your card has been saved.
        </Text>
        <Text as="p">Finishing up — your scheduler will appear in a moment.</Text>
      </Alert>
    );
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} noValidate>
      <Box mb="5">
        <PaymentElement
          onLoadError={({ error: loadError }) => {
            onElementLoadError(loadError);
          }}
        />
      </Box>
      {error ? (
        <Box mb="4">
          <AdminFeeCardError message={error} />
        </Box>
      ) : null}
      <Button type="submit" tone="primary" busy={submitting} disabled={!stripe || !elements}>
        {submitting ? 'Saving…' : 'Save card'}
      </Button>
    </form>
  );
}
